import React, { FC } from 'react';

import { Checkbox } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { Field } from 'client/blocks-redesign/form/Field/Field';
import { Link } from 'client/blocks-redesign/Link/Link';
import { wrapInText } from 'client/lib/i18n-utils';
import { routes } from 'client/lib/routes';
import { SkillAccess } from 'client/model/settings';
import { getAdvancedVoiceControl, getCanViewAsAdmin } from 'client/utils';
import * as keyset from 'i18n/AppForm';

import styles from './VoiceControl.module.scss';

interface Props {
    voiceControlEnable: boolean;
    skillAccess: SkillAccess;
    disabled: boolean;
    onVoiceControlChange?: (checked: boolean) => void;
    isAdvancedVoiceControl: boolean;
    onAdvancedVoiceControlChange?: (checked: boolean) => void;
}

const t = i18n(keyset);

const voiceControlBetaDescription = wrapInText((s) => (
    <Link theme="info" external target="_blank" to={routes.documentation.aliceApps.voiceControlBeta}>
        {s}
    </Link>
))(t('voice-control-apply-beta'));

const advancedVoiceControlDescription = wrapInText((s) => (
    <Link theme="info" external target="_blank" to={routes.documentation.aliceApps.voiceControlBeta}>
        {s}
    </Link>
))(t('voice-control-description-advanced'));

const advancedVoiceCatalogueMarkDescription = wrapInText((s) => (
    <Link theme="info" external target="_blank" to={routes.documentation.aliceApps.voiceControlBeta}>
        {s}
    </Link>
))(t('voice-control-advanced-subtitle'));

export const VoiceControl: FC<Props> = ({
    disabled,
    skillAccess,
    voiceControlEnable,
    onVoiceControlChange,
    isAdvancedVoiceControl,
    onAdvancedVoiceControlChange,
}) => {
    const isUserAdmin = getCanViewAsAdmin();
    const isAdvancedVoiceControlEnable = getAdvancedVoiceControl();

    const displayVoiceControl =
        __appConfig.app.enableAppsVoiceControlForCommonUser || isUserAdmin || isAdvancedVoiceControlEnable;
    const hasVoiceControlAccess =
        (skillAccess === SkillAccess.Private && __appConfig.user.featureFlags.appVoiceControlBeta) ||
        isUserAdmin ||
        isAdvancedVoiceControlEnable;

    const checked = hasVoiceControlAccess ? voiceControlEnable : false;
    const description = isAdvancedVoiceControlEnable
        ? advancedVoiceControlDescription
        : hasVoiceControlAccess
          ? t('voice-control-description')
          : voiceControlBetaDescription;

    const handleVoiceControlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!onVoiceControlChange) return;
        onVoiceControlChange(e.target.checked);
    };

    const handleAdvancedVoiceControlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!onAdvancedVoiceControlChange) return;
        onAdvancedVoiceControlChange(e.target.checked);
    };

    return displayVoiceControl ? (
        <Field fieldTitle={t('voice-control-title')} fieldDescription={description}>
            <Checkbox
                size="l"
                content={t('voice-control-label')}
                className={styles.formElement}
                checked={checked}
                disabled={disabled || !hasVoiceControlAccess}
                onChange={handleVoiceControlChange}
            />
            {isAdvancedVoiceControlEnable && (
                <>
                    <div className={styles.voiceControlCheckboxDescription}>{t('voice-control-subtitle')}</div>
                    <Checkbox
                        size="l"
                        content={t('voice-control-advanced-label')}
                        className={styles.formElement}
                        checked={isAdvancedVoiceControl}
                        disabled={disabled}
                        onChange={handleAdvancedVoiceControlChange}
                    />
                    <div className={styles.voiceControlCheckboxDescription}>
                        {advancedVoiceCatalogueMarkDescription}
                    </div>
                </>
            )}
        </Field>
    ) : null;
};
